import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, type User } from '@chicare/shared';

import { useSession, useStaffLogin } from '@/lib/session';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Logo } from '@/app/BuyerLayout';
// Shared with the gates on purpose: "where does this account belong" answered
// in two places is how a door and a wall come to disagree.
import { homeForRole } from '@/lib/roleHome';

/**
 * Staff sign-in — the admin door, the pharmacy door and the rider door.
 *
 * §10 Phase 0 gives buyers phone OTP. Everyone who works a console additionally
 * holds a password, so an incident at 2am does not depend on an SMS gateway
 * being up — which is exactly when the money console matters most.
 *
 * ── WHY ONE COMPONENT AND THREE DOORS ───────────────────────────────────────
 *
 * Three login screens means three places to get password reset, rate limiting
 * and error copy wrong, and two of them would rot. So the form is shared and
 * the DOOR is a parameter.
 *
 * What it is NOT is one door wearing three signs. It used to be exactly that:
 * mounted at /seller/login and /rider/login but hard-coded to the admin
 * console. A pharmacy owner signed in correctly at their own door, was sent to
 * /admin, and was told by the admin gate that they were a pharmacy account and
 * should ask an administrator to change their role — for doing nothing wrong.
 * Meanwhile the page they had signed in on was headed "Admin" and told them
 * pharmacies sign in with a phone number, which they do not.
 *
 * ── SO WHERE SOMEBODY LANDS IS DECIDED BY THEIR ROLE ────────────────────────
 *
 * Not by which door they happened to use. A pharmacy owner who follows an old
 * /admin/login bookmark still ends up in their own console, because sending
 * somebody to a wall they cannot pass is never the useful answer — and the
 * wall is what they saw before.
 */
type Door = 'admin' | 'pharmacy' | 'rider';

interface DoorInfo {
  /** The word beside the wordmark, so nobody signs in at the wrong door. */
  mark: string;
  heading: string;
  /** Optional: a door whose heading already says everything needs no line. */
  blurb?: string;
  /** Where this door leads when nothing more specific was intended. */
  home: string;
  /** Roles this door's console will actually admit. */
  admits: string[];
  /**
   * Whether to say that everything done here is logged.
   *
   * ── WHY IT IS PER DOOR AND NOT ON EVERY SCREEN ──────────────────────────
   *
   * It is a promise on the staff door and a suspicion on the seller's. A
   * ChiCare administrator can change what buyers are told, approve a refund or
   * suspend a business, and telling them their name is on every one of those is
   * both a deterrent and a protection — it is §9.11 written into the first
   * screen they see.
   *
   * A vendor signing in to look at their own orders is being told, before they
   * have typed anything, that they are being watched. Same sentence, opposite
   * meaning, because the relationship is different.
   */
  audit: boolean;
  /** Local convenience only; stripped from the production bundle. */
  demo: Array<{ label: string; email: string }>;
}

const DOORS: Record<Door, DoorInfo> = {
  admin: {
    mark: 'Admin',
    heading: 'Sign in to the console',
    blurb:
      'For ChiCare operations and finance. Pharmacies and riders have their own sign-in, and buyers sign in with a phone number.',
    home: '/admin',
    // A clinical reviewer signs in at the same door as everybody else on
    // staff. Where they LAND is decided by their role, not by the door — they
    // go straight to the reactions queue, which is the only room they may
    // enter.
    admits: ['admin', 'super_admin', 'pharmacist'],
    // Kept: this door opens on settings, refunds, approvals and suspensions.
    audit: true,
    demo: [
      { label: 'Super admin', email: 'admin@chicare.test' },
      { label: 'Pharmacist', email: 'pharmacist@chicare.test' },
    ],
  },
  pharmacy: {
    // "Seller", because this door is also the one a veterinary clinic, an
    // agrovet store and a farm supplier come through. Naming it Pharmacy is a
    // sign over the entrance saying the other half should go elsewhere — and
    // there is nowhere else to go.
    mark: 'Seller',
    // The heading carries it. "Sign in to sell" above "Sign in to your vendor
    // account" was two sign-ins stacked, so the short line became the heading
    // and the blurb went entirely.
    heading: 'Sign in to your vendor account',
    // ── SHORT, AND SILENT ABOUT WHICH KIND OF VENDOR ────────────────────
    //
    // It used to explain that pharmacies and veterinary sellers both sign in
    // here, and that the password is the one from registration rather than a
    // buyer phone number. Every word of that was true and none of it was news:
    // somebody at this screen already knows they are a vendor, and the only
    // people who needed the pharmacy-or-vet sentence were the ones who had
    // been given a reason to wonder — by the sentence.
    //
    // There is one vendor door. The email decides who signs in and what they
    // land on, so the screen has no reason to ask which kind they are.
    home: '/seller',
    // Admins pass every gate: when a pharmacy rings at 9pm with a dead tablet
    // and a rider waiting, somebody at ChiCare has to be able to act.
    admits: ['pharmacy', 'admin', 'super_admin'],
    // Dropped. A vendor opening their own shop is not the audience for it.
    audit: false,
    demo: [{ label: 'Pharmacy owner', email: 'healthplus-wuse-ii@chicare.test' }],
  },
  rider: {
    mark: 'Rider',
    heading: 'Sign in to start a run',
    blurb: 'Use the details ChiCare set up for you. Riders sign in with an email and a password.',
    home: '/rider',
    admits: ['rider', 'admin', 'super_admin'],
    // Kept: a rider takes custody of medicines, and every handover and failed
    // delivery is signed for against their name (§9.18).
    audit: true,
    demo: [{ label: 'Rider', email: 'rider@chicare.test' }],
  },
};

export function StaffLoginScreen({ door = 'admin' }: { door?: Door }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useSession();
  const login = useStaffLogin();
  const info = DOORS[door];

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Where they were headed before the gate stopped them, if anywhere.
  const intended = (location.state as { from?: string } | null)?.from ?? null;

  /**
   * Honour the intended destination only when this account can actually reach
   * it. Otherwise their own console, which is a place rather than a wall.
   *
   * The door's own `home` is the fallback for nobody in particular. Once there
   * IS somebody, their role decides — a clinical reviewer comes through the
   * staff door like everybody else and belongs in the reactions queue, not on
   * a dashboard their role does not cover.
   */
  const landing = (who: User | null | undefined): string => {
    if (!who) {
      return info.home;
    }

    return info.admits.includes(who.role) ? (intended ?? homeForRole(who.role)) : homeForRole(who.role);
  };

  // Already signed in? Nobody needs to be asked twice.
  if (!isLoading && user) {
    return <Navigate to={landing(user)} replace />;
  }

  const fieldErrors = login.error instanceof ApiError ? login.error.fieldErrors : {};

  const generalError =
    login.error instanceof ApiError && !login.error.isValidation
      ? login.error.message
      : (fieldErrors.email?.[0] ?? null);

  return (
    <main className="grid min-h-dvh place-items-center bg-paper px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-7 flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-[9px] bg-blue">
            <Logo className="h-5 w-5 fill-white" />
          </span>
          <span className="font-display text-[21px] font-extrabold tracking-[-0.02em] text-blue">
            ChiCare
          </span>
          {/* Which door this is. It was always "Admin", on all three. */}
          <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
            {info.mark}
          </span>
        </div>

        <div className="rounded-card border border-line bg-white p-5 sm:p-6">
          <h1 className="m-0 font-display text-[21px] font-bold tracking-[-0.02em]">
            {info.heading}
          </h1>
          {info.blurb ? (
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted">{info.blurb}</p>
          ) : null}

          <form
            className="mt-5 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              login.mutate(
                { email: email.trim(), password },
                { onSuccess: (session) => navigate(landing(session.user), { replace: true }) },
              );
            }}
          >
            <Input
              label={door === 'admin' ? 'Work email' : 'Email'}
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              placeholder={door === 'admin' ? 'you@chicare.ng' : 'you@yourpharmacy.ng'}
              error={fieldErrors.email?.[0]}
              onChange={(event) => setEmail(event.target.value)}
            />

            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              error={fieldErrors.password?.[0]}
              onChange={(event) => setPassword(event.target.value)}
            />

            {generalError ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-danger/25 bg-danger-tint px-3 py-2.5 text-[12px] text-danger"
              >
                <Icon name="alert" size={14} className="mt-px shrink-0" />
                <span>{generalError}</span>
              </div>
            ) : null}

            <Button full type="submit" loading={login.isPending} disabled={!email || !password}>
              Sign in
            </Button>
          </form>

          {/* Every door gets this. A password handed over by a colleague at
              registration is a password that WILL be forgotten, and before
              this existed the only way out was another admin doing it from the
              console — useless at 7am when the shop cannot dispatch and that
              person is asleep. */}
          <p className="m-0 mt-3.5 text-[12px] leading-relaxed">
            <Link
              to={`/${door === 'pharmacy' ? 'seller' : door}/forgot-password`}
              className="font-semibold text-blue no-underline"
            >
              Forgot your password?
            </Link>
          </p>

          {/* The one door with a public way in. A pharmacist who has not
              registered yet is otherwise looking at a form they cannot fill. */}
          {door === 'pharmacy' ? (
            <p className="m-0 mt-4 text-[12px] leading-relaxed text-muted">
              Not registered yet?{' '}
              <Link to="/sell" className="font-semibold text-blue no-underline">
                Sell on ChiCare
              </Link>
            </p>
          ) : null}

          {info.audit ? (
            <p className="mt-4 flex items-start gap-1.5 text-[11px] leading-relaxed text-faint">
              <Icon name="shield" size={13} filled className="mt-px shrink-0 text-verified" />
              Every action you take here is recorded against your name, with a timestamp and a
              written reason.
            </p>
          ) : null}
        </div>

        {import.meta.env.DEV ? (
          <DevCredentials
            accounts={info.demo}
            onFill={(e, p) => {
              setEmail(e);
              setPassword(p);
            }}
          />
        ) : null}
      </div>
    </main>
  );
}

/**
 * Local convenience only. The seeded accounts are already public in the README,
 * and hunting for them mid-task is friction with no security benefit — but this
 * is stripped from the production bundle by the `import.meta.env.DEV` guard.
 *
 * Each door offers only the accounts that door admits. Offering a pharmacy
 * owner on the admin screen is how somebody signs in perfectly and is then told
 * they do not belong.
 */
function DevCredentials({
  accounts,
  onFill,
}: {
  accounts: Array<{ label: string; email: string }>;
  onFill: (email: string, password: string) => void;
}) {
  return (
    <div className="mt-3 rounded-card border border-dashed border-line bg-white/60 p-3.5">
      <p className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-faint">
        Local development only
      </p>
      <div className="flex flex-wrap gap-2">
        {accounts.map((account) => (
          <button
            key={account.email}
            type="button"
            onClick={() => onFill(account.email, 'password')}
            className="min-h-9 rounded-btn border border-line bg-white px-3 text-[11.5px] font-medium text-muted hover:border-blue hover:text-blue"
          >
            {account.label}
          </button>
        ))}
      </div>
    </div>
  );
}
